<template>
  <div class="details-attachments">
    <SwitchStandard
      error-message=""
      name="isExternalAttachments"
      label="Is external attachments"
      help-message-position="left"
      type="standard"
      class="details-attachments__switch-container"
      help-message="Attachments are internal by default, to make it visible to customers
      toggle this button."
      :checked="false"
      :disabled="false"
      :hover="false"
      :active="false"
      :focus="false"
      @onClick="toggleAttachmentVisibility">
    </SwitchStandard>
    <upload-attachment
      :title="`Upload Attachments ${
        isExternalAttachments ? '(visible to customers)' : ''
      }`"
      :multiple="true"
      :maximum="3"
      :supported-file-types="[
        'application/vnd.ms-excel',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'message/rfc822',
        'image/png',
        'image/jpeg',
        'image/webp',
        'application/pdf',
        'application/vnd.ms-outlook',
      ]"
      is-instant-upload
      @uploadFiles="uploadFiles" />
    <p class="details-attachments__list-container">Uploaded attachments</p>
    <div v-for="(property, index) in attachments" :key="property.title">
      <accordian
        :expanded="property.expanded"
        :index="index"
        :item="property"
        @onCardToggle="onAccordianToggle">
        <div slot="accordian-header-content">{{ property.title }}</div>
        <attachment-list
          slot="accordian-body"
          :attachments="property.attachments"
          has-download
          class="details-attachments__list"
          @downloadAttachment="downloadAttachment"
          @removeAttachment="removeAttachment" />
      </accordian>
    </div>
    <dispute-rename-attachments-modal
      v-if="showRenameModal"
      :error-message-file-name-list="errorMessageFileNameList"
      :files-with-same-name="filesWithSameName"
      @onModalClose="onModalClose"
      @scanFilesToUpload="scanFilesToUpload"></dispute-rename-attachments-modal>
  </div>
</template>

<script>
// Actions
import { fileActions } from "@/actions";

// Components
import { showToast } from "@/components/common/Toast";
import SwitchStandard from "@anchor/ui-components/src/components/Switch/Standard.vue";
import DisputeRenameAttachmentsModal from "@/components/DisputeRenameAttachmentsModal/DisputeRenameAttachmentsModal.vue";
// Utils
import AxiosFetchForMsal from "@/utils/axiosForMsal";
import { debounce, getMsalIdToken } from "@/utils/helpers";
import { saveAs } from "file-saver";

export default {
  name: "DisputeAttachments",
  components: {
    SwitchStandard,
    DisputeRenameAttachmentsModal,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    ohpDisputeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      attachments: [],
      isExternalAttachments: false,
      showRenameModal: false,
      errorMessageFileNameList: [],
      filesWithSameName: [],
    };
  },
  watch: {
    data() {
      this.attachmentsStructure();
    },
  },
  created() {
    this.attachmentsStructure();
  },
  methods: {
    onModalClose() {
      this.showRenameModal = false;
      this.filesWithSameName = [];
      this.errorMessageFileNameList = [];
    },
    onAccordianToggle(data, index) {
      event.stopPropagation();
      let newArray = [...this.attachments];
      newArray[index] = {
        ...data,
        expanded: !data.expanded,
      };
      this.attachments = newArray;
    },
    toggleAttachmentVisibility() {
      this.isExternalAttachments = !this.isExternalAttachments;
    },
    attachmentsStructure() {
      let internalAttachments = [],
        externalAttachments = [],
        attachments = [];
      this.data.filter((item) => {
        if (item?.external) {
          externalAttachments.push({
            path: item?.fileName,
            fileName: item?.fileName.slice(1),
            type: item?.fileName.split(".").pop(),
            id: item?.id,
            isDeletable: item?.uploadedBy === "AGENT",
          });
        } else {
          internalAttachments.push({
            path: item?.fileName,
            fileName: item?.fileName.slice(1),
            type: item?.fileName.split(".").pop(),
            id: item?.id,
            isDeletable: item?.uploadedBy === "AGENT",
          });
        }
      });
      attachments.push({
        attachments: externalAttachments,
        title: "External (visible to customers)",
      });
      attachments.push({
        attachments: internalAttachments,
        title: "Internal",
      });
      this.attachments = attachments;
    },
    async downloadAttachment(file) {
      try {
        const { fileName } = file;
        const { data } = await AxiosFetchForMsal(
          fileActions.downloadAttachment(
            this.ohpDisputeId,
            fileName,
            `Bearer ${await getMsalIdToken(this)}`
          )
        );
        saveAs(data, fileName);
      } catch (error) {
        console.error(error);
      }
    },
    async removeAttachment(file) {
      this.$parent.isLoading = true;
      try {
        const { fileName } = file;
        await AxiosFetchForMsal(
          fileActions.deleteAttachment(
            this.ohpDisputeId,
            fileName,
            `Bearer ${await getMsalIdToken(this)}`
          )
        );
        showToast("success", "File Deleted Successfully.");
        this.$emit("updateAttachments");
      } catch (error) {
        console.error(error);
        console.error(error.response.data);
        console.error(error.response.status);
        console.error(error.response.headers);
        showToast("error", "Failed to upload the files, Please try again.");
      }
      this.$parent.isLoading = false;
    },
    uploadFiles: debounce(async function (filesToBeUploaded, callback) {
      if (filesToBeUploaded.length) {
        await this.scanFilesToUpload(filesToBeUploaded, callback);
      }
    }),
    async uploadAttachments(request) {
      try {
        const msalToken = await getMsalIdToken(this);
        const { data, status } = await AxiosFetchForMsal(
          fileActions.uploadAttachments(
            this.ohpDisputeId,
            request,
            `Bearer ${msalToken}`
          )
        );
        if (status === 200) {
          showToast("success", "File Uploaded Successfully.");
          this.$emit("updateAttachments");
        }
      } catch (e) {
        console.error("e", e);
        showToast("error", `Failed to upload files, Please try again.`);
      }
    },

    async scanFilesToUpload(filesToBeUploaded, callback, rename = false) {
      try {
        if (rename) {
          this.onModalClose();
        }
        const msalToken = await getMsalIdToken(this);
        let documentIds = [];
        let failedFiles = [];
        let filesWithSameName = [];
        let errorMessageFileNameList = [];
        const promises = filesToBeUploaded?.map((file) => {
          return new Promise(async (resolve, reject) => {
            const scanResponse = await AxiosFetchForMsal(
              fileActions.scanForAttachments(
                this.ohpDisputeId,
                file,
                `Bearer ${msalToken}`,
                rename
              )
            );

            if (
              scanResponse?.status === 200 &&
              scanResponse?.data?.duplicateFileExist
            ) {
              resolve({
                file,
                errorMessage: scanResponse?.data?.errorMessage,
                duplicateFileExist: scanResponse?.data?.duplicateFileExist,
              });
            }
            if (
              scanResponse?.status === 200 &&
              scanResponse?.data?.documentId
            ) {
              const scanStatusResponse = await AxiosFetchForMsal(
                fileActions.getAttachmentScanStatus(
                  scanResponse?.data?.documentId,
                  `Bearer ${msalToken}`
                )
              );

              if (
                scanStatusResponse?.status === 200 &&
                scanStatusResponse?.data === "Done"
              ) {
                resolve({ file, documentId: scanResponse?.data?.documentId });
              } else {
                reject({ file });
              }
            } else {
              reject({ file });
            }
          });
        });

        await Promise.allSettled(promises)
          .then(async (response) => {
            response.forEach((item) => {
              if (item.status === "fulfilled") {
                if (item?.value?.duplicateFileExist) {
                  filesWithSameName.push(item?.value?.file);
                  errorMessageFileNameList.push(item?.value?.errorMessage);
                } else {
                  documentIds.push({
                    documentId: item?.value?.documentId,
                    external: this.isExternalAttachments,
                  });
                }
              } else {
                failedFiles.push(files);
              }
            });

            if (documentIds.length) {
              await this.uploadAttachments({ request: documentIds });
            }
            if (filesWithSameName.length > 0) {
              this.filesWithSameName = filesWithSameName;
              this.errorMessageFileNameList = errorMessageFileNameList;
              this.showRenameModal = true;
            }
            failedFiles.forEach((failedFile) => {
              showToast(
                "error",
                `Failed to upload the file ${failedFile?.name}`
              );
            });
          })
          .catch((err) => {
            console.error("err", err);
            showToast("error", "Failed to upload the files, Please try again.");
          });
      } catch (error) {
        console.log(error);
        showToast("error", "Failed to upload the files, Please try again.");
      }
      if (typeof callback === "function") {
        callback();
      }
    },
  },
};
</script>

<style lang="scss">
.details-attachments {
  &__switch-container {
    max-width: 40.9rem;
    .form-help {
      padding: 0px 0px 12px 0px !important;
    }
  }
  &__list-container {
    font-family: "Maersk Text Bold";
    color: var(--Blue-900-Darkest);
    margin-bottom: 16px;
  }
  &__list {
    .attachment-list__header {
      margin-bottom: 0.2rem;
    }
    .attachment-list__not-found {
      margin: 0px 1rem 1rem 1rem;
    }
  }
  .accordian__header {
    margin-left: 0px;
  }
  .attachment-list__item {
    background: var(--Gray-50);
  }
  .accordian {
    margin-top: 1rem;
  }
}
</style>
